import styled from '@emotion/styled';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useMedia } from 'react-use';
import { css } from '@emotion/react';

import { Button, Icon, Text, tokens } from '@hotjar/hotsauce';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { INVITE } from '@hotjar/webapp/constants/routes/team';
import { Body, Head, Header, Row, Table } from '@hotjar/webapp/components/GridTable/GridTable';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useIdentityProviderList } from '@hotjar/webapp/domains/sso/hooks/useIdentityProviderList';
import { useHasSSOConnection } from '@hotjar/webapp/domains/sso';
import type { Site as SiteDomainModel } from '@hotjar/site';
import { Event, UiPosition } from '../events';
import { Site } from '../Site/Site';
import { EditOrganizationModal } from './EditOrganization/EditOrganizationModal';
import { DeleteOrganizationModal } from './DeleteOrganization/DeleteOrganizationModal';
import type { OrganizationsListItem, OrganizationTrackingData } from './types';
import { TransferOrganizationModal } from './TransferOrganization/TransferOrganizationModal';
import { OrganizationDropdown } from './OrganizationDropdown';

interface OrganizationProps {
  organization: OrganizationsListItem;
  sitesTotal: number;
  organizationsTotal: number;
  onTrackingCodeClick(site: SiteDomainModel): void;
  onCreateSite(organizationId: number): void;
}

const S = {
  Card: styled.section`
    border: 1px solid ${tokens.color.ui_border};
    padding: ${tokens.spacer.x3} ${tokens.spacer.x4};
    margin-bottom: ${tokens.spacer.x3};
    border-radius: ${tokens.radius.m};
    ${tokens.elevation(1)};
  `,
  Header: styled.div`
    display: flex;
    align-items: center;
  `,
  Title: styled.h3`
    ${tokens.text('subtitle1')};
    margin-left: ${tokens.spacer.x4};
    color: ${tokens.color.text_default};
    ${tokens.truncateText()}
  `,
  Sites: styled.div`
    margin-top: ${tokens.spacer.x6};
  `,
  NoSites: styled.div`
    ${tokens.flexCenter()};
    flex-direction: column;
    padding: ${tokens.spacer.x2};
  `,
};

const TRANSLATION_DOMAIN = 'account:sites-and-organizations';

export function Organization({
  organization,
  sitesTotal,
  organizationsTotal,
  onTrackingCodeClick,
  onCreateSite,
}: OrganizationProps): React.ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { click } = useTrack();
  const [editOrganization, setEditOrganization] = useState(false);
  const [deleteOrganization, setDeleteOrganization] = useState(false);
  const [transferOrganization, setTransferOrganization] = useState(false);
  const isMobile = useMedia(tokens.breakpoint.maxWidth.m);
  const { data: idpData = [] } = useIdentityProviderList(organization?.accountId);
  const isSSOEnabled = useHasSSOConnection(idpData, organization.id);

  const allSitesOnBasic = organization.sites.every((site) => site.plan === 'basic');
  const canDelete =
    allSitesOnBasic && organizationsTotal > 1 && organization.sites.length < sitesTotal;
  const hasSites = organization.sites.length > 0;

  const trackingData: OrganizationTrackingData = {
    account_id: organization.accountId,
    organization_id: organization.id,
  };

  const openModal = (event: Event, setFn: Dispatch<SetStateAction<boolean>>) => {
    setFn(true);
    click(event, UiPosition.ORGANIZATION_SETTINGS_MENU, trackingData);
  };

  const organizationLabel = `organization-${organization.id}`;

  return (
    <>
      <S.Card aria-labelledby={organizationLabel}>
        <S.Header>
          <Icon icon="building_business" inline size="l" />
          <S.Title id={organizationLabel}>{organization.name}</S.Title>
          <OrganizationDropdown
            trackingData={trackingData}
            hasSites={hasSites}
            organizationId={`${organization.id}`}
            accountId={`${organization.accountId}`}
            allSitesOnBasic={allSitesOnBasic}
            canDelete={canDelete}
            onInvite={() => {
              navigateTo(INVITE.path, { organizationId: organization.id });
              click(Event.CLICK_INVITE_PEOPLE, UiPosition.ORGANIZATION_SETTINGS_MENU, trackingData);
            }}
            onUpdate={() => openModal(Event.CLICK_EDIT_ORGANIZATION, setEditOrganization)}
            onTransfer={() => openModal(Event.CLICK_TRANSFER_OWNERSHIP, setTransferOrganization)}
            onDelete={() => openModal(Event.CLICK_DELETE_ORGANIZATION, setDeleteOrganization)}
          />
        </S.Header>
        {organization.sites.length ? (
          <S.Sites>
            <Table templateColumns="max-content auto max-content max-content max-content max-content">
              <Head>
                <Row>
                  <Header>{t('table.headers.id')}</Header>
                  <Header>{t('table.headers.site-name')}</Header>
                  <Header>{!isMobile && t('table.headers.tracking')}</Header>
                  <Header>{t('table.headers.2fa-required')}</Header>
                  <Header>{t('table.headers.plan')}</Header>
                  <Header>{}</Header>
                </Row>
              </Head>
              <Body>
                {organization.sites.map((site) => (
                  <Site
                    site={site}
                    isMobile={isMobile}
                    key={site.id}
                    sitesTotal={sitesTotal}
                    onTrackingCodeClick={onTrackingCodeClick}
                    isSSOEnabled={isSSOEnabled}
                  />
                ))}
              </Body>
            </Table>
          </S.Sites>
        ) : (
          <S.NoSites>
            <Text as="p" textType="subtitle2">
              <Icon
                icon="tray_empty"
                inline
                css={css`
                  margin-right: ${tokens.spacer.x1};
                `}
              />
              {t('organization.no-sites')}
            </Text>
            <Button
              icon="plus"
              kind="secondary"
              size="s"
              css={css`
                margin-top: ${tokens.spacer.x1};
              `}
              onClick={() => onCreateSite(organization.id)}
            >
              {t('new-site')}
            </Button>
          </S.NoSites>
        )}
      </S.Card>
      {editOrganization && (
        <EditOrganizationModal
          organization={organization}
          opened={editOrganization}
          handleSuccess={() => setEditOrganization(false)}
          handleCancel={() => setEditOrganization(false)}
        />
      )}
      {deleteOrganization && (
        <DeleteOrganizationModal
          organization={organization}
          opened={deleteOrganization}
          handleSuccess={() => setDeleteOrganization(false)}
          handleCancel={() => setDeleteOrganization(false)}
        />
      )}
      {transferOrganization && (
        <TransferOrganizationModal
          opened={transferOrganization}
          handleSuccess={() => setTransferOrganization(false)}
          handleCancel={() => setTransferOrganization(false)}
          organization={organization}
        />
      )}
    </>
  );
}
